import React, { useState, useEffect } from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import { useOfferFormContext } from "./OfferFormContext";

// Version: 1.1 - Compact layout and relative positioning to avoid container growth
const CategorySelection = ({
  setSubCategories,
  stepIndex,
  setStepIndex,
  categoryId,
  setCategoryId,
  setPayloadData,
  categories,
  editOfferData,
  isEdit,
}) => {
  const { registerNextAction, setNextLabel, setIsNextDisabled } = useOfferFormContext();
  const [selectedCategory, setSelectedCategory] = useState(null);

  useEffect(() => {
    setNextLabel("Next");
  }, [setNextLabel]);

  // Initial data loading
  useEffect(() => {
    if (categories?.length > 0) {
      if (editOfferData?.category && !selectedCategory) {
        const cat = categories.find((c) => c._id === editOfferData.category);
        if (cat) {
          setSelectedCategory(cat);
          setCategoryId(cat._id);
          // NEW: Ensure subcategories are passed up if we are loading from edit data or re-visiting
          if (Array.isArray(cat.subCategory)) {
            setSubCategories(cat.subCategory);
          }
        }
      } else if (categoryId && !selectedCategory) {
        const cat = categories.find((c) => c._id === categoryId);
        if (cat) {
          setSelectedCategory(cat);
          // NEW: Ensure subcategories are passed up if we are re-visiting
          if (Array.isArray(cat.subCategory)) {
            setSubCategories(cat.subCategory);
          }
        }
      }
    }
  }, [categories, editOfferData, categoryId, setCategoryId, selectedCategory, setSubCategories]);

  const formik = useFormik({
    initialValues: {
      category: categoryId || "",
    },
    validationSchema: Yup.object({
      category: Yup.string().required("Category is required"),
    }),
    onSubmit: (values) => {
      setPayloadData((prev) => ({ ...prev, category: values.category }));
      setStepIndex(stepIndex + 1);
    },
  });

  useEffect(() => {
    registerNextAction(() => formik.handleSubmit());
  }, [formik.values, registerNextAction]);

  useEffect(() => {
    setIsNextDisabled(!formik.values.category);
  }, [formik.values.category, setIsNextDisabled]);

  const handleClick = (id) => {
    const cat = categories.find((c) => c._id === id);
    setSelectedCategory(cat);
    setCategoryId(id);
    formik.setFieldValue("category", id);
    // NEW: Set subcategories from the selected category, or empty if none
    if (cat && Array.isArray(cat.subCategory)) {
      setSubCategories(cat.subCategory);
    } else {
      setSubCategories([]);
    }
  };

  const isCategoryClicked = (cat) => {
    return selectedCategory?._id === cat?._id;
  };

  const blueFilter = "invert(53%) sepia(95%) saturate(2070%) hue-rotate(167deg) brightness(95%) contrast(102%)";
  const greyFilter = "invert(88%) sepia(0%) saturate(0%) hue-rotate(0deg) brightness(103%) contrast(92%)";

  return (
    <div className="w-full flex flex-col items-center justify-start flex-grow">
      {/* Target Content: Spacing matched to reference image */}
      <div className="w-full max-w-[700px] mt-[30px] md:mt-[60px]">
        <div className="mb-6 md:mb-10 w-full text-left">
          <h2 className="text-[22px] md:text-[32px] font-[600] text-[#303030] font-poppins text-center">
            Which <span className="text-[#00ADEF]">Category</span> Best Describes your Offer?
          </h2>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-5" data-field="category">
          {categories?.length > 0 &&
            categories?.map((cat) => {
              const clicked = isCategoryClicked(cat);
              return (
                <div
                  key={cat._id}
                  className={`flex flex-col items-start justify-between p-5 h-[115px] rounded-[10px] border cursor-pointer transition-all duration-200 ${clicked
                    ? "border-2 border-[#00ADEF] bg-[#EBF9FE] shadow-sm"
                    : "border-[#E0E0E0] bg-white hover:border-[#BDBDBD]"
                    }`}
                  onClick={() => handleClick(cat?._id)}
                >
                  <div className="mb-2">
                    <img
                      src={process.env.REACT_APP_ICON_URL + cat?.image}
                      alt={cat?.title}
                      className="w-[28px] h-[28px]"
                      style={clicked ? { filter: blueFilter } : { filter: greyFilter, opacity: 0.55 }}
                    />
                  </div>
                  <p className={`font-poppins font-[500] text-[14px] ${clicked ? "text-[#00ADEF]" : "text-[#303030]"}`}>
                    {cat?.title}
                  </p>
                </div>
              );
            })}
        </div>

        {formik.errors?.category && formik.touched?.category ? (
          <span className="text-red-600 sm:text-[10px] md:text-[12px] font-[600] ms-1 mt-3 block text-center">
            Category is required.
          </span>
        ) : null}
      </div>
    </div>
  );
};

export default CategorySelection;
